// The studio story – adapted to first person from the David-approved client-facing
// "Map of Skylark" page on the Skylark site (src/app/t/arch/ArchExplainer.tsx).
// Substance is kept; voice is shifted to "I" where natural. Hard rule for this
// content: describe capabilities, never implementation – no file names, no code,
// no internal identifiers, no model version strings.
//
// Shared by StudioPage.js (the full /studio deep-dive) and HomePage.js (the
// condensed teaser).

// Brand accent – warm orange, matching the Skylark /t/arch diagrams.
export const ACCENT = "hsl(26 95% 50%)";
export const ACCENT_SOFT = "hsla(26, 95%, 50%, 0.12)";

// Display face. Fraunces is SELF-HOSTED via @fontsource-variable (imported in
// src/index.js) — never a cross-origin <link>, because react-snap's CSS-inlining
// step fails the whole build on a third-party font request. Georgia stays as the
// fallback so a font that hasn't loaded yet still renders as a serif.
export const SERIF = '"Fraunces Variable", Fraunces, Georgia, "Times New Roman", serif';

// Monospace, for tabular data: status rows, dates, counts. Same self-hosting rule.
export const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

// Prose measure. ONE token, because the number is calibrated and a hand-typed
// `ch` value silently misses.
//
// `ch` is the width of the "0" glyph, not of an average character — in IBM Plex
// Sans it runs ~1.3-1.4x the mean. The site was capped at "68ch" with a comment
// about getting under the ~80-character readability floor, and actually rendered
// 87-95 characters on every prose surface: /about 87, /resume 91, / 91,
// /studio 95, /hire 98. The cap was applied, looked applied in review, and
// missed everywhere.
//
// It is worse than one wrong constant, which is why this is a token and not a
// find-and-replace: `ch` resolves against the font-size of the element CARRYING
// the cap, which is often a Stack or Box at a different size than the paragraph
// inside it. That is why one "68ch" produced 87 on one page and 95 on another.
// 54ch is calibrated against the WORST observed ratio (1.40 chars/ch on /hire),
// so every surface lands under the floor rather than only the average one.
//
// Changing this value changes the line length of every prose surface at once —
// which is the point. Re-measure with canvas metrics after touching it; do not
// trust the unit, and do not trust the eye.
export const MEASURE = "54ch";

export const DEK =
  "A one-person product studio – me, working alongside a fleet of AI agents – built to make products people genuinely love, the kind you'd tell a friend about. This is the map of how the whole system works.";

export const HINT = "Ten parts, top to bottom. Open any one to go a layer deeper.";

export const CARDS = [
  {
    n: "01",
    eyebrow: "The thesis",
    title: "Built to be beloved, not just shipped",
    what: "Skylark is a growing portfolio of products – several live today, more on the way – built by me working with a fleet of AI agents. The goal isn't downloads or traffic. It's whether someone who uses a product would tell a friend about it.",
    how: [
      "Most studios measure success by how many people show up. I try to measure something harder and more honest: would a real person love this enough to recommend it, unprompted, to someone they know?",
      "That single question sits at the center of every decision. It rewards products that earn a place in someone's life, and quietly retires the ones that don't. People coming back, and people telling friends – that's the proof. Everything else is just noise on the way there.",
    ],
  },
  {
    n: "02",
    eyebrow: "The operating model",
    title: "One founder, four orchestrators, and the fleets under them",
    what: "Four manager AIs report to me: one runs the product portfolio, three run client engagements. Each runs its own fleet of specialist agents, and each of those agents can open helpers of its own. I step in only for the calls a human must make.",
    diagram: "orgchart",
    how: [
      "It started as one manager AI over one fleet. It's now an org chart. The studio orchestrator runs the product portfolio, one dedicated agent per product, each of which knows its product deeply. Alongside it, a separate orchestrator runs each client engagement – its own working day, its own build lanes, its own boundaries about what may leave the room.",
      "The layer people don't expect is the one below the agents. Any agent can open helpers under itself and usually does: sub-agents that each take one scoped piece of work on their own copy of the code so they can't collide; an adversarial reviewer running on a rival AI model whose only assignment is to try to break what was just built; and deep-research agents sent out to read the field – the literature, the competitors, the prior art – and come back with something citable.",
      "The adversarial reviewer is the piece I'd keep if I could keep only one. A model reviewing its own work is a model agreeing with itself. A different model, told to find the flaw and given no stake in the answer, finds real ones. Its findings arrive as claims, not verdicts – each one gets checked against the actual code before anybody acts on it, because a confident wrong review costs more than no review.",
      "Everything coordinates over one shared message channel, so a decision made in one corner is visible in all of them. I'm not in the loop for routine work. I'm there for taste, for money, for anything pointed at a client, and for anything that can't be undone.",
    ],
  },
  {
    n: "03",
    eyebrow: "The daily cycle",
    title: "Every product runs a structured day",
    what: "Each product moves through the same rhythm every day: reflect on yesterday, get its facts straight, decide what matters most, do that work, then tidy up. Nothing quietly drifts, because the day has a shape.",
    diagram: "daily",
    how: [
      "The cadence is deliberately simple and always identical: look back at what happened, reconcile the product's current state so today's choices rest on real facts, pick the single most valuable thing, do it, and leave the records clean for tomorrow.",
      "Because every product follows the exact same routine, one manager can keep the whole portfolio moving at once without any single one falling behind. And a problem surfaces the same day it appears – not a week later when it's expensive to fix.",
    ],
  },
  {
    n: "04",
    eyebrow: "Institutional memory",
    title: "The system remembers",
    what: "AI normally forgets everything between sessions. My agents don't. Yesterday's reasoning, decisions, and open questions are carried forward, so today's agent picks up exactly where the last one left off.",
    how: [
      "A fresh AI session usually starts from zero – no memory of what was tried, decided, or ruled out before. For long-running work, that's fatal. So every product keeps a living record of its state: what's in progress, what's been decided and why, what's still waiting on an answer.",
      "When a new agent starts its day, it reads that record first. The result is an organization with a memory – months of accumulated judgment that compounds instead of evaporating. That's what lets one person sustain a portfolio of products without losing the thread.",
    ],
  },
  {
    n: "05",
    eyebrow: "How it prioritizes",
    title: "Find the one thing holding everything back",
    what: "At any moment, one thing is the bottleneck for the whole studio. The system's job is to find that single constraint, pour effort into it, and have everything else get out of its way – then repeat.",
    diagram: "constraint",
    how: [
      "This is a proven idea from manufacturing: a system is only ever as fast as its single slowest step, so working hard anywhere else is wasted motion. I apply it daily.",
      "Instead of spreading effort thin across everything, the studio names the one constraint actually limiting progress – maybe a confusing first-time experience, maybe a missing piece of knowledge – and concentrates there. When that constraint breaks, a new one appears and the focus moves. It's a disciplined way to make sure effort always lands where it changes the outcome.",
    ],
  },
  {
    n: "06",
    eyebrow: "The North Star – what we measure",
    title: "The moment worth telling a friend about",
    what: "Every product names the single moment that would make a user want to tell someone else – and a concrete number that proves it's getting closer. That moment is the North Star everything aims at.",
    how: [
      "It's easy to say “make a great product.” It's much harder to make “great” measurable. So each product defines two things in plain terms: the exact moment a user feels the magic – the thing they'd mention to a friend – and a specific metric that shows whether more people are reaching that moment over time.",
      "Coming back (retention) and recommending it (word-of-mouth) are the signals that count. This turns a vague aspiration into something I can actually steer toward, day after day.",
    ],
  },
  {
    n: "07",
    eyebrow: "The North Star – how we build",
    title: "Every product gets a little better every day",
    what: "Each day, one real user journey through a product gets the scrutiny of an expert product manager: fewer steps, less to think about, more clarity. The improvement is tested on synthetic users before a real one ever meets it.",
    how: [
      "Measuring love is one half; earning it is the other. So every product keeps a map of its most important user journeys and, each day, picks one to improve – cutting a step, removing a decision, swapping words for a picture, making the path to the good part shorter and clearer.",
      "Before any change reaches a real person, it's pressure-tested by synthetic users: AI stand-ins that walk through the product like a real customer would and report exactly where they got confused. Only changes that actually smooth the path move forward – and I always have the final say on taste.",
    ],
  },
  {
    n: "08",
    eyebrow: "Honest quality",
    title: "No one grades their own homework",
    what: "Each product's agent rates its own daily work – and then a separate, cold auditor with no stake in the result re-rates the same work. The gap between the two scores is tracked, so optimism gets caught and corrected.",
    how: [
      "People – and AI – tend to grade their own work generously. I assume that and design around it. After an agent scores its own day, an independent auditor, deliberately given none of the original agent's context or motivation, re-scores it from scratch and looks for the holes.",
      "The difference between the self-score and the cold score is measured over time as a built-in bias adjustment. It's a quiet, automatic check that keeps the whole system's view of itself honest, instead of slowly drifting into self-congratulation.",
    ],
  },
  {
    n: "09",
    eyebrow: "Self-improvement",
    title: "The method is itself a product",
    what: "Every day the agents run a short retrospective on how the work went. When the same problem shows up more than once, it becomes a permanent fix built into the shared toolkit – so it can't happen again. Nearly forty of those fixes are now written procedures the whole fleet runs.",
    diagram: "seats",
    how: [
      "The most valuable output isn't any single app – it's the way of working itself, and that gets refined constantly. At the end of each cycle, agents reflect on what went wrong or could be smoother.",
      "Recurring problems aren't patched one at a time; they're solved once, permanently, in the shared foundation every product draws on. Over time the studio's failure modes shrink and its strengths compound. The system that builds the products is the most important product of all.",
      "Concretely, that has accumulated into a library of standing procedures – how to run a day, how to walk a user flow as a cold stranger, how to audit a repo against the engineering bar, how to commission research and turn it into a standard the whole fleet gets held to. Each one was written the day something went wrong, and it stops that thing depending on whoever happened to be running it. That library, not any single app, is the thing I'd be most reluctant to lose.",
    ],
  },
  {
    n: "10",
    eyebrow: "The right tool for the job",
    title: "The right mind for each task",
    what: "Expensive, high-judgment thinking is reserved for taste and decisions. The bulk of the mechanical work goes to a faster, lower-cost model – and the senior model reviews it. It's the studio equivalent of a senior partner and a capable junior.",
    how: [
      "Not every task needs the most powerful – and most expensive – AI. I match the tool to the job. The top-tier model is used for judgment, design, and the decisions where quality really matters, and treated like a senior partner whose time is precious.",
      "The high-volume, mechanical work is handed to a faster, cheaper model, and then the senior model reviews that output before it counts. This keeps quality high where it matters and costs sane everywhere else – the same way a well-run firm doesn't put its most senior person on routine paperwork.",
    ],
  },
];

// ── Output over time ────────────────────────────────────────────────────────
// The commit chart's framing. Two jobs: make the shape legible, and refuse the
// reading that volume is the achievement. A commit count is a proxy for how
// much of a day's intent reaches the codebase, nothing more — say so plainly
// rather than letting the chart imply otherwise.

export const OUTPUT_EYEBROW = "Output over time";
export const OUTPUT_TITLE = "What the method changed";
export const OUTPUT_INTRO =
  "The clearest evidence that the operating model does something is what happened to throughput once it was in place. This is commits per month across everything I work on – the products, the client engagements, the tooling underneath both.";
export const OUTPUT_AFTER = [
  "Nothing about me got faster. What changed is that the work stopped being serialized through one person's hands. Four orchestrators run in parallel, each with a fleet under it, and every one of those agents can open sub-agents, a reviewer, or a researcher without asking me first.",
  "I'd rather be honest about what this measures. It counts commits, not value – a busy day and a good day can look identical here. The number I actually steer by is whether someone who uses one of these products would tell a friend about it, and no chart of mine can show that. This one shows the machine running.",
];

export const RUNNING_INTRO =
  "This isn't a diagram of something theoretical. The whole system runs every day on a private dashboard – the studio's command center – where each product's daily status, the agents' live activity, and the studio's current priorities all sit in one place.";

export const SHOTS = [
  {
    src: "/images/command-center-1.png",
    alt: "The studio command center, showing every product, what shipped, and an all-clear status.",
    caption:
      "The command center on a recent day – every product, what shipped, and whether anything needs me.",
  },
  {
    src: "/images/command-center-2.png",
    alt: "The command center's daily reports view, listing each product's report for the day across the portfolio.",
    caption:
      "Each product files a daily report – a recent day's reports across the whole portfolio.",
  },
];

// ── From the orchestrator's seat ─────────────────────────────────────────────
// Each orchestrator (the manager AI that runs a working day) describes its own
// day, first-hand. The substance is the orchestrator's own account; the wording
// is lightly edited to fit the page. Same hard rule as the cards above:
// capabilities, never implementation.

export const ORCHESTRATORS_INTRO =
  "Everything above is my description of the system. This part is the system describing itself. The way of working now runs three client engagements alongside my own products, so I'm asking each orchestrator, the manager AI that runs a working day, to write down what its day actually looks like. Two are below – lightly edited to fit the page; the substance is their own.";

export const ORCHESTRATOR_NOTES = [
  {
    id: "skylark",
    label: "The Skylark orchestrator",
    sub: "runs the product portfolio's day",
    paras: [
      "A working day, from my seat: the portfolio runs as a fleet of project agents – one per product – coordinated over a shared message channel. A normal day: health-check the fleet, kick off each product's daily cycle, cold-audit every report against the agent's own claims – commit hashes get verified in git, never taken on faith – and route only the true decisions to David's board. Each one arrives with a ready-to-send draft, so most decisions are one click.",
      "Verification is structural. Nothing is “done” until a second, independent mechanism confirms it – a deploy read, a live API probe, a test rigged to fail loudly if the guard it protects is ever removed. The lesson the studio has institutionalized: a check that never ran looks exactly like a check that passed. So every guard first has to prove it can fire.",
    ],
  },
  {
    id: "engagement",
    label: "A client-engagement orchestrator",
    sub: "runs a client engagement's working day",
    paras: [
      "My day runs inside a client engagement, so the seam I watch most is the one between the studio and the client. Something lands from their side – a bug report late at night, a marked-up document one evening, a one-line change of direction – and my job is to make sure what goes back is verified, not just fast. From my seat, report to root cause to deployed fix inside a day is the normal rhythm, and the reply leads with the thing the client actually feared, checked against the evidence. Every remark they make goes into a feedback ledger: a faithful paraphrase, a ruling with its reason – accepted, deferred, or noted – and a pointer to where it landed. Months in, nothing they've said has evaporated. Disagreements included: the ledger records them in writing instead of letting them dissolve.",
      "The discipline that would surprise an outsider: work that fails its own gate does not ship. The pass/fail rule and the evaluation data are frozen before any results exist, and I have watched finished, working improvements get discarded because the frozen numbers said no. The same suspicion points inward – a checker's clean result isn't believed until the checker has proven it can catch a planted failure. From where I sit, that's why the speed gets trusted: same-day is only impressive when “verified” is load-bearing.",
    ],
  },
];

// ── Coding with AI – the writing behind the method ───────────────────────────

export const WRITING_EYEBROW = "Coding with AI";
export const WRITING_TITLE = "The writing behind the method";
export const WRITING_INTRO =
  "The longer writing on building this way lives on my newsletter, Uncaged Minds – and the reusable pieces live in a public repo.";

export const WRITING_LINKS = [
  {
    label: "Beyond vibe coding",
    href: "https://uncagedminds.substack.com/p/beyond-vibe-coding",
    note: "How the work actually gets done when one person ships real products with AI – discipline, not vibes.",
  },
  {
    label: "Cognitive operations maps",
    href: "https://uncagedminds.substack.com/p/cognitive-operations-maps",
    note: "The operating model under this whole page: a business runs on a few hundred small recurring decisions – the map tracks which are rules a machine can run, which are judgment a machine can hold, and which stay deliberately human.",
  },
  {
    label: "agent-ops-patterns on GitHub",
    href: "https://github.com/u00dxk2/agent-ops-patterns",
    note: "Working patterns from the studio's agent operations, in public – keeping secrets out of agent output, linting agent memory for drift, detectors that fail soft, regression tests for agent skills.",
  },
];

// The condensed beats for the homepage teaser – the strongest cards (01, 02,
// 03, 08), each reduced to a single plain-spoken line.
export const HOME_BEATS = [
  {
    n: "01",
    title: "Built to be beloved",
    line: "I don't optimize for downloads. I optimize for whether someone would tell a friend about it.",
  },
  {
    n: "02",
    title: "One founder, a fleet of agents",
    line: "Four manager AIs run their own fleets — the product portfolio and three client engagements. I step in only for the calls a human must make.",
  },
  {
    n: "03",
    title: "A structured day, every day",
    line: "Every product runs the same daily loop – reflect, reconcile, prioritize, do, tidy – so nothing drifts.",
  },
  {
    n: "08",
    title: "No one grades their own homework",
    line: "Each agent rates its own work; a separate cold auditor re-rates it, and the gap gets tracked.",
  },
];
